<!--
  TeleworkListDialog.vue
    Telework List Dialog Component
-->

<!-- -------------------------------------------------------------------------------- -->
<template>
  <v-dialog
    v-model="mDialog"
    fullscreen
    hide-overlay
    transition="slide-x-transition"
    scrollable
  >
    <v-card tile>
      <v-toolbar card dark color="primary" app class="hm-iphonex-notch">
        <v-btn icon color="primary" @click="onClickClose">
          <v-icon>close</v-icon>
        </v-btn>
        <v-toolbar-title>{{ $t("settings.teleworklist") }}</v-toolbar-title>
      </v-toolbar>

      <v-card-text
       id="hm-twl-container"
       class="hm-twl-container"
      >
      <div class="hm-iphonex-notch">
        <v-flex class="hm-twl-outline" xs11>
          <!-- 年月選択 -->
          <v-layout row align-center justify-center>
            <div>
              <v-chip
              color="primary"
              text-color="white"
              @click="onClickPrevMonth"
              label
              >＜<span class="hm-display-monitor"> 前月</span></v-chip>
            </div>
            <div>
              <v-layout row align-center justify-center>
                <div class="hm-twl-datesel">
                  <v-select
                    v-model="mSelectedYear"
                    item-text="label"
                    item-value="value"
                    :items="mSelectYearlist"
                    @change="onChangeYear($event)"
                    class="hm-twl-datesel-select"
                  />
                </div>
                <div class="hm-twl-datesel">
                  <v-select
                    v-model="mSelectedMonth"
                    item-text="label"
                    item-value="value"
                    :items="mSelectMonthlist"
                    @change="onChangeMonth($event)"
                    class="hm-twl-datesel-select"
                  />
                </div>
              </v-layout>
            </div>
            <div>
              <v-chip
                color="primary"
                text-color="white"
                @click="onClickNextMonth"
                label
              ><span class="hm-display-monitor">翌月 </span>＞</v-chip>
            </div>
          </v-layout>

          <!-- 勤務時間集計 -->
          <div>
            <table class="hm-twl-wh-total-table">
              <tr>
                <td class="hm-twl-wh-total-table-cell">出勤日数</td>
                <td class="hm-twl-wh-total-table-cell">休暇日数</td>
                <td class="hm-twl-wh-total-table-cell">勤務時間 合計</td>
                <td class="hm-twl-wh-total-table-cell">離席時間 合計</td>
              </tr>
              <tr>
                <td class="hm-twl-wh-total-table-cell-data">{{mWorkingHoursTotal.working_days}}</td>
                <td class="hm-twl-wh-total-table-cell-data">{{mWorkingHoursTotal.holiday_days}}</td>
                <td class="hm-twl-wh-total-table-cell-data">{{mWorkingHoursTotal.working_hours}}</td>
                <td class="hm-twl-wh-total-table-cell-data">{{mWorkingHoursTotal.leaving_hours}}</td>
              </tr>
            </table>
          </div>

          <!-- 勤務時間データ一覧表 -->
          <v-layout row wrap align-center justify-center class="hm-twl-head">
            <v-flex class="hm-twl-head-cell hm-twl-head-cell-day">日付</v-flex>
            <v-flex class="hm-twl-head-cell hm-twl-head-cell-holiday">休暇種別</v-flex>
            <v-flex class="hm-twl-head-cell">出勤時刻</v-flex>
            <v-flex class="hm-twl-head-cell">離席</v-flex>
            <v-flex class="hm-twl-head-cell">着席</v-flex>
            <v-flex class="hm-twl-head-cell">退勤時刻</v-flex>
            <v-flex class="hm-twl-head-cell hm-twl-head-cell-wh">勤務時間</v-flex>
            <v-flex class="hm-twl-head-cell hm-twl-head-cell-lh">離席時間</v-flex>
          </v-layout>
          <div id="hm-twl-data-area" class="hm-twl-data-area">
            <telework-list-row-info
              @teleworkInfoSelected="onTeleworkSelected"
              @holidayInfoSelected="onHolidaySelected"
              v-for="teleworkInfo in mSelectTeleworkList"
              :key="teleworkInfo.workinghour_id"
              :pTeleworkInfo="teleworkInfo"
              class="hm-twl-data-outline"
            ></telework-list-row-info>
          </div>
        </v-flex>
      </div>
      </v-card-text>
    </v-card>

    <!-- Dialog定義 -->
    <errorinfo-dialog ref="errorInfoDialog">
      <div slot="msg">{{ mErrMsg }}</div>
    </errorinfo-dialog>
    <systeminfo-dialog ref="systemInfoDialog">
      <div slot="msg">{{ mSysMsg }}</div>
    </systeminfo-dialog>
    <telework-edit-dialog ref="teleworkEditDialog" />
    <holiday-dialog ref="holidayDialog" />

  </v-dialog>
</template>

<!-- -------------------------------------------------------------------------------- -->
<script>
import { mapState } from 'vuex'
import { getNowDatetimeMoment, formatDateMoment, calcMonth } from '@/common'
import TeleworkListRowInfo from '@/components/TeleworkListRowInfo'
import ErrorInfoDialog from '@/components/dialogs/ErrorInfoDialog'
import SystemInfoDialog from '@/components/dialogs/SystemInfoDialog'
import TeleworkEditDialog from '@/components/dialogs/TeleworkEditDialog'
import HolidayInfoDialog from '@/components/dialogs/HolidayInfoDialog'

export default {
  name: 'TeleworkListDialog',
  data: () => ({
    mDialog: false,
    mErrMsg: '',
    mSysMsg: '',
    mConfirmMsg: '',
    mSelectedYear: '',
    mSelectYearlist: [],
    mSelectedMonth: '',
    mSelectMonthlist: [],
    mSelectTeleworkList: null,
  }),
  components: {
    'telework-list-row-info': TeleworkListRowInfo,
    'errorinfo-dialog': ErrorInfoDialog,
    'systeminfo-dialog': SystemInfoDialog,
    'telework-edit-dialog': TeleworkEditDialog,
    'holiday-dialog': HolidayInfoDialog,
  },
  mounted: function () {
    this.$log.debug('# TeleworkListDialog mouted')
    this.$on('showTeleworkList', this.showTeleworkList)
  },
  destroyed: function () {
    this.$log.debug('# TeleworkListDialog.')
  },
  computed: {
    ...mapState({
      mTeleworkList: state => state.telework.sTeleworkList,
      mWorkingHoursTotal: state => state.telework.sWorkingHoursTotal,
    }),
  },
  methods: {
    // -------------------------------------------------------------------------
    //  勤務時間データ画面表示
    showTeleworkList: function () {
      //this.$log.debug('★[showTeleworkList]')
      this.$store.commit('setIsWhereaboutsPage', false)  // 行動予定一覧の更新を行わない
      this.createSelectDateList()
      this.getTeleworkList()
      this.mDialog = true           // Show Dialog
    },
    // -------------------------------------------------------------------------
    //  勤務時間データ取得
    getTeleworkList: async function () {
      //this.$log.debug('★[getTeleworkList]')
      //this.$log.debug('companyId:' + this.$store.getters.loginUserCompanyId + ', staffId:' + this.$store.getters.loginUserBaseInfo.staff_id + ')')
      //this.$log.debug('year：' + this.mSelectedYear + ', month：' + this.mSelectedMonth)

      const result = await this.$store.dispatch(
        'aGetTeleworkList',
        { companyId: this.$store.getters.loginUserCompanyId,
          memberId: this.$store.getters.loginUserBaseInfo.staff_id,
          date: this.mSelectedYear + this.mSelectedMonth
        }
      )
      if (!result.error && !result.body.error) {
        // storeのデータを取得
        this.mSelectTeleworkList = this.mTeleworkList
        //this.mDispWorkingHoursTotal = this.mWorkingHoursTotal
        document.getElementById('hm-twl-data-area').scrollTop = 0   // 表の先頭を表示
      }
      else {
        this.mErrMsg = 'データの取得に失敗しました。'
        this.$refs.errorInfoDialog.$emit('show')
      }
    },
    // -------------------------------------------------------------------------
    //  年月セレクトリスト作成
    createSelectDateList: function () {
      // 現在年月取得
      this.mSelectedYear = formatDateMoment(getNowDatetimeMoment(), 'YYYY')
      this.mSelectedMonth = formatDateMoment(getNowDatetimeMoment(), 'MM')

      // 年セレクトリスト
      const year = Number(this.mSelectedYear)
      let year_data
      for (let i = 4; i >= 0; i--) {
        year_data = {label: String(year - i) + '年', value: String(year - i)}
        this.mSelectYearlist.push(year_data)
      }

      // 月セレクトリスト
      let month_data
      for (let i = 1; i <= 12; i++) {
        month_data = {label: String(i) + '月', value: ('0' + String(i)).slice(-2)}
        this.mSelectMonthlist.push(month_data)
      }
    },
    // -------------------------------------------------------------------------
    //  年を選択
    onChangeYear: function (event) {
      this.mSelectedYear = event
      this.getTeleworkList()
    },
    // -------------------------------------------------------------------------
    //  月を選択
    onChangeMonth: function (event) {
      this.mSelectedMonth = event
      this.getTeleworkList()
    },
    // -------------------------------------------------------------------------
    //  前月ボタンクリック
    onClickPrevMonth: function () {
      if (this.mSelectedYear === this.mSelectYearlist[0].value &&
          this.mSelectedMonth === this.mSelectMonthlist[0].value) {
        // 選択できる最小の年月を選択中の場合は、それ以上前月は表示できない
        this.mSysMsg = 'これ以上過去のデータを表示できません。'
        this.$refs.systemInfoDialog.$emit('show')
        return
      }

      // 前の月を取得
      let input_date = this.mSelectedYear + this.mSelectedMonth
      const prev_date = calcMonth(input_date, -1)
      this.mSelectedYear = prev_date.slice(0, 4)
      this.mSelectedMonth = prev_date.slice(4, 6)

      this.getTeleworkList()
    },
    // -------------------------------------------------------------------------
    //  翌月ボタンクリック
    onClickNextMonth: function () {
      if (this.mSelectedYear === this.mSelectYearlist[4].value &&
          this.mSelectedMonth === this.mSelectMonthlist[12 - 1].value) {
        // 選択できる最大の年月を選択中の場合は、それ以上翌月は表示できない
        this.mSysMsg = 'これ以上未来のデータを表示できません。'
        this.$refs.systemInfoDialog.$emit('show')
        return
      }

      // 次の月を取得
      let input_date = this.mSelectedYear + this.mSelectedMonth
      const next_date = calcMonth(input_date, 1)
      this.mSelectedYear = next_date.slice(0, 4)
      this.mSelectedMonth = next_date.slice(4, 6)

      this.getTeleworkList()
    },
    // -------------------------------------------------------------------------
    //  画面クローズ
    onClickClose: function () {
      this.$store.commit('setIsWhereaboutsPage', true)  // 行動予定一覧の更新を行う
      this.mDialog = false
    },
    // -------------------------------------------------------------------------
    //  更新有無判定
    checkUpdated: function (updated) {
      return (updated === 1)
    },
    onTeleworkSelected: function (send_obj) {
      // 編集ダイアログ表示
      this.$refs.teleworkEditDialog.$emit(
        'onClickTeleworkEdit',
        send_obj
      )
    },
    onHolidaySelected: function (send_obj) {
      // 編集ダイアログ表示
      this.$refs.holidayDialog.$emit(
        'onClickHolidayInfo',
        send_obj
      )
    },
  },
}
</script>

<!-- -------------------------------------------------------------------------------- -->
<style scoped>
.hm-twl-container {
  margin-top: calc(64px - 16px);   /* v-cardのpadding16pxをヘッダにかぶせる */
}
@media screen and (min-width: 601px) {
  /* 幅601px～ */
  .hm-twl-container {
    width: calc(100vw - 17px);
  }
}
.hm-twl-outline {
  /*max-width: 550px !important;*/
  margin: 0 auto;
  width: 100%;
}
@media screen and (min-width: 601px) {
  /* 幅601px～ */
  .hm-twl-outline {
    max-width: 650px !important;
  }
}
.hm-twl-datesel {
  margin: 0 auto;
  width: 45%;
}
.hm-twl-datesel-select {
  font-size: 15px;
}
/*.hm-twl-table {
  border: 1px solid #bcbcbc;
}
.hm-twl-table tbody td {
  text-align: center;
  height: 35px;
}
.hm-twl-td-hover {
  transition: all 0.1s ease 0s;
}
.hm-twl-td-hover:hover {
  background: #dbdbdb;
}
.hm-twl-td-updated {
  font-weight: bold;
  color: #005FFF;
}*/
.hm-twl-wh-total-table {
  border-collapse: collapse;
  margin: 0 auto 10px auto;
}
.hm-twl-wh-total-table-cell,
.hm-twl-wh-total-table-cell-data {
  border: solid 1px rgba(0,0,0,.12);
  height: 30px;
  padding: 0 25px;
  font-size: 0.9em;
  /*font-size: 12px;*/
  text-align: center;
}
@media screen and (max-width: 645px) {
  /* 幅～645px */
  .hm-twl-wh-total-table-cell {
    padding: 0 5px;
  }
}
.hm-twl-data-outline {
  border-bottom: solid 1px rgba(0,0,0,.12);
}
.hm-twl-data-outline:last-child {
  border-bottom: none;
}
.hm-twl-head {
  height: 55px;
  line-height: 55px;
  border: solid 1px rgba(0,0,0,.12);
  border-bottom: none;
}
.hm-twl-head-cell {
  height: 100%;
  width: 80px;
  text-align: center;
  font-size: 12px;
}
.hm-twl-head-cell-day {
  border-right: solid 1px rgba(0,0,0,.12);
}
.hm-twl-head-cell-holiday {
  border-right: solid 1px rgba(0,0,0,.12);
}
.hm-twl-head-cell-wh {
  border-left: solid 1px rgba(0,0,0,.12);
}
@media screen and (max-width: 645px) {
  /* 幅～645px(アプリ版) */
  .hm-twl-head-cell {
    width: calc(100% / 6);
  }
  .hm-twl-head-cell-wh,
  .hm-twl-head-cell-lh {
    display: none;
  }
}
.hm-twl-data-area {
  overflow-y: scroll;
  height: calc((100vh - 275px) - var(--iphonex-notch));    /* タイトル・年月選択・勤務時間合計・表ヘッダの空きの分を差し引き */
  border: solid 1px rgba(0,0,0,.12);
  -ms-overflow-style:none;
}
@media screen and (min-width: 601px) {
  /* 幅601px～ */
  .hm-twl-data-area {
    height: calc(100vh - 275px);
  }
}
.hm-twl-data-area::-webkit-scrollbar{
 display:none;
}
</style>



// WEBPACK FOOTER //
// src/components/dialogs/TeleworkListDialog.vue